<template>
  <ModalForm
    v-model="isModalOpen"
    :data="businessInfo as unknown as Record<string, unknown> ?? {}"
    :title="$t('pages.onboarding.sole_trader.edit_business.title')"
    :on-submit="handleForm"
    disable-submit-on-error>
    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_business.subtitle.info') }}
    </p>
    <div class="grid grid-cols-2 gap-24">
      <FormKit
        type="CountryDropdown"
        name="countryIso3"
        :label="$t('form.label.country_of_registration')"
        value-key="shortName"
        :options="PERMITTED_COUNTRIES"
        validation="required"/>

      <FormKit
        type="DateInput"
        name="dateOfBusinessStart"
        :label="$t('form.label.starting_date_of_trading_activity')"
        :validation="`required|date_before:${new Date()}`"/>

      <FormKit
        type="TextPatternInput"
        name="businessLegalName"
        :label="$t('form.label.business_name')"
        :pattern="businessNameRule.pattern"
        :validation="businessNameRule.validation"/>

      <FormKit
        type="TextPatternInput"
        name="vatNumber"
        :label="$t('form.label.vat_number')"
        :pattern="VatFormKitRule.pattern"/>

      <FormKit
        type="SearchDropdown"
        name="businessIndustry"
        :label="$t('pages.onboarding.business.company_details.info.data.business_industry')"
        :options="businessIndustries"
        value-key="value"
        validation="required"/>

      <FormKit
        type="text"
        name="email"
        :label="$t('form.label.email')"
        :placeholder="$t('placeholder.input.optional')"
        validation="email|length:2"/>

      <FormKit
        type="text"
        name="website"
        :label="$t('form.label.business_website')"
        :placeholder="$t('placeholder.input.optional')"
        validation="website"/>

      <FormKit
        type="TextPatternTextArea"
        name="businessActivityDescription"
        outer-class="col-span-2"
        wrapper-class="w-full max-w-full"
        inner-class="h-135"
        :label="$t('form.label.business_description')"
        :pattern="BusinessActivityFormKitRule.pattern"
        :validation="BusinessActivityFormKitRule.validation"/>
    </div>

    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_business.subtitle.turnover') }}
    </p>
    <div class="grid grid-cols-2 gap-24">
      <FormKit
        type="SearchDropdown"
        :label="$t('pages.onboarding.admin_page.estimated_annual_turnover')"
        name="annualTurnover"
        :options="annualTurnovers"
        label-key="label"
        value-key="value"
        validation="required"/>
    </div>
  </ModalForm>
</template>

<script setup lang="ts">
import ModalForm from '@/components/modals/ModalForm.vue';
import { PERMITTED_COUNTRIES } from '@/constants';
import { BusinessActivityFormKitRule } from '@/rules/formkit-rules/BusinessActivity';
import { NameFormKitRule } from '@/rules/formkit-rules/Name';
import { VatFormKitRule } from '@/rules/formkit-rules/Vat';
import { onboardingNameWithDigitsRegExp } from '@/rules/regexp/Name';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import {
  onboardingAdminSoleTraderStoreActions as action,
  useOnboardingAdminSoleTraderStore,
} from '@/stores/onboarding/OnboardingAdminSoleTrader';
import { getOptionsList } from '@/utils';

const isModalOpen = defineModel<boolean>('isModalOpen', { required: true });

const { businessInfo } = useOnboardingAdminSoleTraderStore();

const businessIndustries = getOptionsList('BusinessIndustry');
const annualTurnovers = getOptionsList('AnnualTurnover');

const handleForm = async (data: Onboarding.SoleTrader.BusinessInfo) => {
  action.updateBusinessInfo(data, {
    onSuccess: () => {
      isModalOpen.value = false;
    },
  });
};

// Rules
const businessNameRule = NameFormKitRule(100, onboardingNameWithDigitsRegExp);
</script>

<style lang="scss" scoped>
.subheader {
  @apply font-semibold text-22;
}
</style>
